import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Chip,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  AccountBalanceWalletOutlined,
  TrendingUp,
  TrendingDown,
  SwapHorizOutlined,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useAdjustWalletBalanceMutation } from '../services';

const AdjustWalletModal = ({
  open,
  onClose,
  currentBalance = 0,
  currency = '€',
  onSuccess,
}) => {
  const [adjustWallet, { isLoading }] = useAdjustWalletBalanceMutation();

  // Mode: 'direct' | 'incremental'
  const [mode, setMode] = useState('direct');

  // Direct Mode fields
  const [targetBalance, setTargetBalance] = useState('');

  // Incremental Mode fields
  const [incAction, setIncAction] = useState('add'); // 'add' | 'subtract'
  const [incAmount, setIncAmount] = useState('');

  // Optional notes field
  const [notes, setNotes] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setMode('direct');
      setTargetBalance(
        currentBalance != null && !isNaN(currentBalance)
          ? parseFloat(currentBalance).toFixed(2)
          : '0.00'
      );
      setIncAction('add');
      setIncAmount('');
      setNotes('');
    }
  }, [open, currentBalance]);

  // Handlers for simple text input (no number spinners or mousewheel increment/decrement)
  const handleTargetBalanceChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
      setTargetBalance(val);
    }
  };

  const handleIncAmountChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
      setIncAmount(val);
    }
  };

  if (!open) return null;

  const currentBalNum = parseFloat(currentBalance) || 0;

  // Calculation for Direct Mode
  const targetNum = parseFloat(targetBalance);
  const isValidTarget =
    targetBalance !== '' && targetBalance !== '.' && !isNaN(targetNum) && targetNum >= 0;
  const directDelta = isValidTarget
    ? Math.round((targetNum - currentBalNum) * 100) / 100
    : 0;

  // Calculation for Incremental Mode
  const incNum = parseFloat(incAmount);
  const isValidInc =
    incAmount !== '' && incAmount !== '.' && !isNaN(incNum) && incNum > 0;
  const incNewBalance = isValidInc
    ? incAction === 'add'
      ? Math.round((currentBalNum + incNum) * 100) / 100
      : Math.round((currentBalNum - incNum) * 100) / 100
    : currentBalNum;
  const isIncNegative = isValidInc && incAction === 'subtract' && incNum > currentBalNum;

  // Validate form submission
  let isSubmitDisabled = false;
  let validationMessage = '';

  if (mode === 'direct') {
    if (!isValidTarget) {
      isSubmitDisabled = true;
      validationMessage = 'Please enter a valid wallet balance (0 or greater).';
    } else if (directDelta === 0) {
      isSubmitDisabled = true;
      validationMessage = 'New balance is identical to the current balance.';
    }
  } else {
    if (!isValidInc) {
      isSubmitDisabled = true;
      validationMessage = 'Please enter a valid amount greater than 0.';
    } else if (isIncNegative) {
      isSubmitDisabled = true;
      validationMessage = `Cannot deduct ${currency}${incNum.toFixed(2)}: exceeds current balance (${currency}${currentBalNum.toFixed(2)}).`;
    }
  }

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isSubmitDisabled || isLoading) return;

    try {
      let payload = {};
      if (mode === 'direct') {
        payload = {
          mode: 'direct',
          target_balance: targetNum,
          notes: notes.trim() || undefined,
        };
      } else {
        payload = {
          mode: 'incremental',
          action: incAction,
          amount: incNum,
          notes: notes.trim() || undefined,
        };
      }

      const res = await adjustWallet(payload).unwrap();
      toast.success(res?.message || 'Wallet balance adjusted successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adjusting wallet balance:', error);
      toast.error(error?.data?.message || error?.message || 'Failed to adjust wallet balance');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '20px 24px',
          width: '100%',
          maxWidth: '560px',
          boxShadow: '0px 10px 35px rgba(0, 0, 0, 0.15)',
          position: 'relative',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 18,
          top: 18,
          color: '#4B5563',
          border: '1.5px solid #D1D5DB',
          width: 32,
          height: 32,
          padding: 0,
          borderRadius: '50%',
          '&:hover': {
            bgcolor: '#F3F4F6',
            borderColor: '#6B7280',
            color: '#111827',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Modal Header */}
      <Box sx={{ pr: 5, mb: 1.8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#111827',
            }}
          >
            <AccountBalanceWalletOutlined fontSize="small" />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '19px',
              color: '#111827',
              letterSpacing: '-0.3px',
            }}
          >
            Adjust Wallet Balance
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '13px' }}>
          Update the physical cash float maintained in the restaurant wallet / safe.
        </Typography>
      </Box>

      {/* Current Balance Banner */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.2,
          bgcolor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          mb: 1.8,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '11.5px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Current Safe Balance
          </Typography>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', mt: 0.1 }}>
            {currency}
            {currentBalNum.toFixed(2)}
          </Typography>
        </Box>
        <Chip
          label="Active Safe Balance"
          size="small"
          sx={{
            bgcolor: '#ECFDF5',
            color: '#047857',
            fontWeight: 600,
            fontSize: '11px',
            height: '24px',
            border: '1px solid #A7F3D0',
          }}
        />
      </Box>

      {/* Mode Selector Tabs */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          bgcolor: '#F3F4F6',
          p: '4px',
          borderRadius: '10px',
          mb: 1.8,
          gap: '4px',
        }}
      >
        <Button
          onClick={() => setMode('direct')}
          sx={{
            py: 0.7,
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            bgcolor: mode === 'direct' ? '#000000' : 'transparent',
            color: mode === 'direct' ? '#FFFFFF' : '#4B5563',
            boxShadow: mode === 'direct' ? '0px 2px 4px rgba(0, 0, 0, 0.12)' : 'none',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: mode === 'direct' ? '#1F2937' : '#E5E7EB',
              color: mode === 'direct' ? '#FFFFFF' : '#111827',
            },
          }}
          startIcon={<SwapHorizOutlined sx={{ fontSize: 18, color: 'inherit' }} />}
        >
          Direct Set Balance
        </Button>
        <Button
          onClick={() => setMode('incremental')}
          sx={{
            py: 0.7,
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            bgcolor: mode === 'incremental' ? '#000000' : 'transparent',
            color: mode === 'incremental' ? '#FFFFFF' : '#4B5563',
            boxShadow: mode === 'incremental' ? '0px 2px 4px rgba(0, 0, 0, 0.12)' : 'none',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: mode === 'incremental' ? '#1F2937' : '#E5E7EB',
              color: mode === 'incremental' ? '#FFFFFF' : '#111827',
            },
          }}
          startIcon={<TrendingUp sx={{ fontSize: 18, color: 'inherit' }} />}
        >
          Add / Deduct Cash
        </Button>
      </Box>

      {/* Form Content */}
      <form onSubmit={handleSubmit}>
        {/* ── MODE 1: DIRECT ── */}
        {mode === 'direct' && (
          <Box sx={{ mb: 1.8 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', mb: 0.6 }}>
              New Wallet Balance ({currency})
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="text"
              inputProps={{ inputMode: 'decimal' }}
              value={targetBalance}
              onChange={handleTargetBalanceChange}
              placeholder="0.00"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  fontSize: '14.5px',
                  fontWeight: 600,
                },
              }}
            />

            {/* Live Delta Explanation */}
            {isValidTarget && (
              <Box
                sx={{
                  mt: 1.2,
                  px: 1.5,
                  py: 0.9,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor:
                    directDelta > 0
                      ? '#ECFDF5'
                      : directDelta < 0
                      ? '#FEF2F2'
                      : '#F9FAFB',
                  border: `1px solid ${
                    directDelta > 0
                      ? '#A7F3D0'
                      : directDelta < 0
                      ? '#FECACA'
                      : '#E5E7EB'
                  }`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color:
                      directDelta > 0
                        ? '#065F46'
                        : directDelta < 0
                        ? '#991B1B'
                        : '#6B7280',
                  }}
                >
                  {directDelta > 0
                    ? `Adjustment: +${currency}${directDelta.toFixed(2)} will be added (Credit)`
                    : directDelta < 0
                    ? `Adjustment: -${currency}${Math.abs(directDelta).toFixed(2)} will be deducted (Debit)`
                    : 'Balance unchanged (0.00 difference)'}
                </Typography>
                <Chip
                  label={directDelta > 0 ? 'Inflow (+)' : directDelta < 0 ? 'Outflow (-)' : 'Neutral'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '11px',
                    height: '22px',
                    bgcolor: directDelta > 0 ? '#10B981' : directDelta < 0 ? '#EF4444' : '#9CA3AF',
                    color: '#FFFFFF',
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* ── MODE 2: INCREMENTAL (ADD / DEDUCT) ── */}
        {mode === 'incremental' && (
          <Box sx={{ mb: 1.8 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', mb: 0.6 }}>
              Action Type
            </Typography>
            <RadioGroup
              row
              value={incAction}
              onChange={(e) => setIncAction(e.target.value)}
              sx={{ mb: 1.5, gap: 1.5 }}
            >
              <Box
                onClick={() => setIncAction('add')}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '10px',
                  border: `1.5px solid ${incAction === 'add' ? '#059669' : '#E5E7EB'}`,
                  bgcolor: incAction === 'add' ? '#ECFDF5' : '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <FormControlLabel
                  value="add"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        p: '4px',
                        mr: 0.5,
                        color: '#059669',
                        '&.Mui-checked': { color: '#059669' },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUp sx={{ fontSize: 17, color: '#059669' }} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#065F46' }}>
                        + Add Cash
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>

              <Box
                onClick={() => setIncAction('subtract')}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '10px',
                  border: `1.5px solid ${incAction === 'subtract' ? '#DC2626' : '#E5E7EB'}`,
                  bgcolor: incAction === 'subtract' ? '#FEF2F2' : '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <FormControlLabel
                  value="subtract"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        p: '4px',
                        mr: 0.5,
                        color: '#DC2626',
                        '&.Mui-checked': { color: '#DC2626' },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingDown sx={{ fontSize: 17, color: '#DC2626' }} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#991B1B' }}>
                        - Deduct Cash
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>
            </RadioGroup>

            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', mb: 0.6 }}>
              Amount to {incAction === 'add' ? 'Add' : 'Deduct'} ({currency})
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="text"
              inputProps={{ inputMode: 'decimal' }}
              value={incAmount}
              onChange={handleIncAmountChange}
              placeholder="0.00"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  fontSize: '14.5px',
                  fontWeight: 600,
                },
              }}
            />

            {/* Calculated Result Preview */}
            {isValidInc && (
              <Box
                sx={{
                  mt: 1.2,
                  px: 1.5,
                  py: 0.9,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: isIncNegative ? '#FEF2F2' : '#F9FAFB',
                  border: `1px solid ${isIncNegative ? '#FECACA' : '#E5E7EB'}`,
                }}
              >
                <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: isIncNegative ? '#991B1B' : '#374151' }}>
                  {isIncNegative
                    ? `Insufficient balance: max deductible is ${currency}${currentBalNum.toFixed(2)}`
                    : `New Balance will be: ${currency}${incNewBalance.toFixed(2)}`}
                </Typography>
                {!isIncNegative && (
                  <Chip
                    label={incAction === 'add' ? `+${currency}${incNum.toFixed(2)}` : `-${currency}${incNum.toFixed(2)}`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: '11px',
                      height: '22px',
                      bgcolor: incAction === 'add' ? '#ECFDF5' : '#FEF2F2',
                      color: incAction === 'add' ? '#047857' : '#B91C1C',
                      border: `1px solid ${incAction === 'add' ? '#A7F3D0' : '#FECACA'}`,
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Notes (Optional) */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', mb: 0.6 }}>
            Notes / Reference (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional audit memo or cash drawer reference"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#FFFFFF',
                fontSize: '13.5px',
              },
            }}
          />
        </Box>

        {/* Validation warning */}
        {validationMessage && (
          <Typography sx={{ color: '#DC2626', fontSize: '12.5px', fontWeight: 500, mb: 2 }}>
            {validationMessage}
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              height: '40px',
              px: 2.5,
              color: '#4B5563',
              fontWeight: 600,
              fontSize: '14px',
              bgcolor: '#F3F4F6',
              '&:hover': { bgcolor: '#E5E7EB' },
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitDisabled || isLoading}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              height: '40px',
              px: 3,
              bgcolor: '#111827',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '14px',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#000000',
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
              },
              '&.Mui-disabled': {
                bgcolor: '#9CA3AF',
                color: '#F3F4F6',
              },
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              'Save Adjustment'
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default AdjustWalletModal;
